import React from 'react';
import {
  Container, Card, Table, Button, Modal, Form, Row, Col,
  Spinner, Alert, InputGroup, Badge,
} from 'react-bootstrap';
import {
  PlusIcon, PencilSquareIcon, TrashIcon,
  MagnifyingGlassIcon, TruckIcon,
} from '@heroicons/react/24/solid';
import CompanyLayout from '../common/CompanyLayout';
import {
  useCompanySuppliers,
  ORDER_CHANNEL_OPTIONS,
  CURRENCY_OPTIONS,
  DAY_OPTIONS,
} from './hooks/useCompanySuppliers';

const thStyle: React.CSSProperties = {
  color: '#475569', fontWeight: 600, fontSize: '0.8rem',
  textTransform: 'uppercase', letterSpacing: '0.5px', padding: '12px 16px',
};

const CompanySuppliers: React.FC = () => {
  const {
    isExpired, loading, search, setSearch, filtered, suppliers,
    showModal, editingSupplier, form, setForm, saving, feedback, setFeedback,
    deleteState, handleOpenCreate, handleOpenEdit, toggleDeliveryDay,
    handleSave, handleConfirmDelete, handleDelete, closeModal, closeDeleteModal,
  } = useCompanySuppliers();

  if (loading) {
    return (
      <CompanyLayout>
        <Container className="py-5 text-center">
          <Spinner animation="border" variant="success" />
          <p className="mt-2 text-muted">Loading suppliers...</p>
        </Container>
      </CompanyLayout>
    );
  }

  return (
    <CompanyLayout>
      <Container fluid className="py-4 px-3 px-md-4 page-content-padding">
        {feedback.success && (
          <Alert variant="success" dismissible onClose={() => setFeedback(p => ({ ...p, success: '' }))} className="mb-3">
            {feedback.success}
          </Alert>
        )}
        {feedback.error && !showModal && (
          <Alert variant="danger" dismissible onClose={() => setFeedback(p => ({ ...p, error: '' }))} className="mb-3">
            {feedback.error}
          </Alert>
        )}

        {/* Header */}
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center gap-2 mb-2 mb-md-0">
            <div style={{ backgroundColor: '#ecfdf5', borderRadius: '8px', padding: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <TruckIcon style={{ width: '22px', height: '22px', color: '#059669' }} />
            </div>
            <h4 className="mb-0 fw-bold" style={{ color: '#1e293b' }}>Suppliers</h4>
            <Badge bg="" style={{ backgroundColor: '#ecfdf5', color: '#059669', fontSize: '0.75rem', fontWeight: '600', padding: '4px 10px', borderRadius: '6px' }}>
              {suppliers.length}
            </Badge>
          </div>
          <Button variant="success" size="sm" className="d-flex align-items-center gap-1"
            onClick={handleOpenCreate} disabled={isExpired} style={{ padding: '6px 14px' }}>
            <PlusIcon style={{ width: '15px', height: '15px' }} />
            Add Supplier
          </Button>
        </div>

        {/* Search */}
        <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '10px' }}>
          <Card.Body className="py-3">
            <Row className="g-2 align-items-center">
              <Col xs={12} md={6}>
                <InputGroup size="sm">
                  <InputGroup.Text style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px 0 0 6px' }}>
                    <MagnifyingGlassIcon style={{ width: '15px', height: '15px', color: '#94a3b8' }} />
                  </InputGroup.Text>
                  <Form.Control placeholder="Search by name or contact..." value={search}
                    onChange={e => setSearch(e.target.value)}
                    style={{ border: '1px solid #e2e8f0', borderRadius: '0 6px 6px 0' }} />
                </InputGroup>
              </Col>
              <Col className="text-muted" style={{ fontSize: '0.85rem' }}>
                {filtered.length} of {suppliers.length} suppliers
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Table */}
        <Card className="border-0 shadow-sm" style={{ borderRadius: '10px' }}>
          <div className="table-responsive">
            <Table hover className="mb-0 align-middle">
              <thead>
                <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                  <th style={thStyle}>Supplier</th>
                  <th style={thStyle} className="d-none d-md-table-cell">Contact</th>
                  <th style={thStyle} className="d-none d-lg-table-cell">Lead Time</th>
                  <th style={thStyle} className="d-none d-lg-table-cell">Order Channel</th>
                  <th style={thStyle} className="d-none d-md-table-cell">Min Order</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-5 text-muted">
                      <TruckIcon style={{ width: '32px', height: '32px', opacity: 0.3, marginBottom: '8px' }} />
                      <div>{search ? 'No suppliers match your search.' : 'No suppliers yet. Add your first supplier.'}</div>
                    </td>
                  </tr>
                ) : (
                  filtered.map(s => (
                    <tr key={s.id}>
                      <td style={{ padding: '12px 16px' }}>
                        <div className="fw-500" style={{ color: '#1e293b', fontWeight: 500 }}>{s.name}</div>
                        {s.email && <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{s.email}</div>}
                      </td>
                      <td style={{ padding: '12px 16px', color: '#64748b' }} className="d-none d-md-table-cell">
                        {s.contact_person || '—'}
                        {s.phone && <div style={{ fontSize: '0.8rem' }}>{s.phone}</div>}
                      </td>
                      <td style={{ padding: '12px 16px', color: '#64748b' }} className="d-none d-lg-table-cell">
                        {s.lead_time_days != null ? `${s.lead_time_days} days` : '—'}
                      </td>
                      <td style={{ padding: '12px 16px' }} className="d-none d-lg-table-cell">
                        {s.preferred_order_channel_display
                          ? <Badge bg="" style={{ backgroundColor: '#f1f5f9', color: '#475569', fontWeight: 500 }}>{s.preferred_order_channel_display}</Badge>
                          : <span style={{ color: '#94a3b8' }}>—</span>}
                      </td>
                      <td style={{ padding: '12px 16px', color: '#64748b' }} className="d-none d-md-table-cell">
                        {s.min_order_value ? `${s.min_order_currency} ${parseFloat(s.min_order_value).toFixed(2)}` : '—'}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div className="d-flex gap-1">
                          <Button variant="outline-secondary" size="sm" onClick={() => handleOpenEdit(s)} style={{ padding: '4px 8px' }} title="Edit">
                            <PencilSquareIcon style={{ width: '14px', height: '14px' }} />
                          </Button>
                          <Button variant="outline-danger" size="sm" onClick={() => handleConfirmDelete(s)} style={{ padding: '4px 8px' }} title="Delete">
                            <TrashIcon style={{ width: '14px', height: '14px' }} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </Card>
      </Container>

      {/* Add / Edit Modal */}
      <Modal show={showModal} onHide={closeModal} size="lg" centered>
        <Modal.Header closeButton style={{ borderBottom: '1px solid #e2e8f0' }}>
          <Modal.Title style={{ fontSize: '1.1rem', fontWeight: 600, color: '#1e293b' }}>
            {editingSupplier ? 'Edit Supplier' : 'Add New Supplier'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {feedback.error && <Alert variant="danger" className="mb-3">{feedback.error}</Alert>}

          <div style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#9ca3af', marginBottom: '12px' }}>Basic Information</div>
          <Row className="g-3 mb-3">
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Supplier Name <span className="text-danger">*</span></Form.Label>
                <Form.Control size="sm" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g., Local Mill Co." />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Contact Person</Form.Label>
                <Form.Control size="sm" value={form.contact_person} onChange={e => setForm(p => ({ ...p, contact_person: e.target.value }))} placeholder="e.g., John Smith" />
              </Form.Group>
            </Col>
          </Row>

          <div style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#9ca3af', marginBottom: '12px' }}>Contact Details</div>
          <Row className="g-3 mb-3">
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Email</Form.Label>
                <Form.Control size="sm" type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="orders@supplier.com" />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Phone</Form.Label>
                <Form.Control size="sm" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="+1 234 567 8900" />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>WhatsApp</Form.Label>
                <Form.Control size="sm" value={form.whatsapp} onChange={e => setForm(p => ({ ...p, whatsapp: e.target.value }))} placeholder="+1 234 567 8900" />
              </Form.Group>
            </Col>
          </Row>

          <hr className="my-3" style={{ borderColor: '#e2e8f0' }} />
          <div style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#9ca3af', marginBottom: '12px' }}>Delivery Settings</div>
          <Row className="g-3 mb-3">
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Lead Time (days)</Form.Label>
                <Form.Control size="sm" type="number" min="0" value={form.lead_time_days} onChange={e => setForm(p => ({ ...p, lead_time_days: e.target.value }))} placeholder="e.g., 3" />
                <Form.Text className="text-muted">Days between order and delivery</Form.Text>
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Preferred Order Channel</Form.Label>
                <Form.Select size="sm" value={form.preferred_order_channel} onChange={e => setForm(p => ({ ...p, preferred_order_channel: e.target.value }))}>
                  <option value="">Select channel</option>
                  {ORDER_CHANNEL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Delivery Days</Form.Label>
              <div className="d-flex flex-wrap gap-2 mt-1">
                {DAY_OPTIONS.map(d => (
                  <Button key={d.value} variant={form.delivery_days.includes(d.value) ? 'success' : 'outline-secondary'}
                    size="sm" onClick={() => toggleDeliveryDay(d.value)}
                    style={{ minWidth: '52px', padding: '4px 10px', fontSize: '0.8rem' }}>
                    {d.label}
                  </Button>
                ))}
              </div>
              <Form.Text className="text-muted">Days when this supplier delivers</Form.Text>
            </Col>
          </Row>

          <hr className="my-3" style={{ borderColor: '#e2e8f0' }} />
          <div style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#9ca3af', marginBottom: '12px' }}>Business Terms</div>
          <Row className="g-3">
            <Col xs={12} md={6}>
              <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Minimum Order Value</Form.Label>
              <InputGroup size="sm">
                <Form.Control type="number" min="0" step="0.01" value={form.min_order_value} onChange={e => setForm(p => ({ ...p, min_order_value: e.target.value }))} placeholder="e.g., 100" />
                <Form.Select value={form.min_order_currency} onChange={e => setForm(p => ({ ...p, min_order_currency: e.target.value }))} style={{ maxWidth: '80px' }}>
                  {CURRENCY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                </Form.Select>
              </InputGroup>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Payment Terms</Form.Label>
                <Form.Control size="sm" value={form.payment_terms} onChange={e => setForm(p => ({ ...p, payment_terms: e.target.value }))} placeholder="e.g., Net 30, On Delivery" />
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group>
                <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Notes</Form.Label>
                <Form.Control as="textarea" rows={2} size="sm" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Any additional notes..." />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid #e2e8f0' }}>
          <Button variant="outline-secondary" size="sm" onClick={closeModal} disabled={saving}>Cancel</Button>
          <Button variant="success" size="sm" onClick={handleSave} disabled={saving}>
            {saving ? <><Spinner size="sm" animation="border" className="me-1" />Saving...</> : (editingSupplier ? 'Save Changes' : 'Add Supplier')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation */}
      <Modal show={deleteState.showModal} onHide={closeDeleteModal} centered size="sm">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1rem', fontWeight: 600 }}>Delete Supplier</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">Delete <strong>{deleteState.supplier?.name}</strong>? This cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" size="sm" onClick={closeDeleteModal} disabled={deleteState.deleting}>Cancel</Button>
          <Button variant="danger" size="sm" onClick={handleDelete} disabled={deleteState.deleting}>
            {deleteState.deleting ? <><Spinner size="sm" animation="border" className="me-1" />Deleting...</> : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>
    </CompanyLayout>
  );
};

export default CompanySuppliers;
